import { defineStore } from 'pinia'
import { store } from '../index'
import { setCssVar, humpToUnderline } from '@/utils'
import { colorIsDark, hexToRGB, lighten, mix } from '@/utils/color'
import { ElMessage, ComponentSize } from 'element-plus'
import { useCssVar } from '@vueuse/core'
import { unref } from 'vue'
import { useDark } from '@vueuse/core'
import { securePiniaStorage } from '@/utils/securePinia'

interface AppState {
  /**開發模式 */
  NODE_ENV: string
  /**API 基礎路徑 */
  API_BASE_PATH: string
  /**進行中的請求 */
  LoadingPromises: Promise<any>[]

  breadcrumb: boolean
  breadcrumbIcon: boolean
  collapse: boolean
  uniqueOpened: boolean
  hamburger: boolean
  screenfull: boolean
  size: boolean
  locale: boolean
  tagsView: boolean
  tagsViewIcon: boolean
  logo: boolean
  fixedHeader: boolean
  greyMode: boolean
  dynamicRouter: boolean
  serverDynamicRouter: boolean
  pageLoading: boolean
  layout: LayoutType
  title: string
  isDark: boolean
  currentSize: ComponentSize
  sizeMap: ComponentSize[]
  mobile: boolean
  footer: boolean
  theme: ThemeTypes
  fixedMenu: boolean
}

export const useAppStore = defineStore('app', {
  state: (): AppState => {
    let PATH_URL = import.meta.env.VITE_API_BASE_PATH

    if (!PATH_URL.endsWith('/')) {
      PATH_URL += '/'
    }

    return {
      NODE_ENV: import.meta.env.VITE_NODE_ENV, // 標題
      API_BASE_PATH: PATH_URL, // API 基礎路徑
      LoadingPromises: [], // 進行中的請求

      sizeMap: ['default', 'large', 'small'],
      mobile: false, // 是否是移動端
      title: import.meta.env.VITE_APP_TITLE, // 標題
      pageLoading: false, // 路由跳轉loading
      breadcrumb: true, // 麵包屑
      breadcrumbIcon: true, // 麵包屑圖標
      collapse: false, // 折叠菜單
      uniqueOpened: false, // 是否只保持一個子菜單的展开
      hamburger: true, // 折叠圖標
      screenfull: true, // 全屏圖標
      size: true, // 尺寸圖標
      locale: true, // 多語言圖標
      tagsView: true, // 標籤頁
      tagsViewIcon: true, // 是否顯示標籤圖標
      logo: true, // logo
      fixedHeader: true, // 固定toolheader
      footer: true, // 顯示頁尾
      greyMode: false, // 是否開始灰色模式，用於特殊悼念日
      dynamicRouter: true, // 是否動態路由
      serverDynamicRouter: true, // 是否服務端渲染動態路由
      fixedMenu: false, // 是否固定菜單

      layout: 'classic', // layout布局
      isDark: false, // 是否是暗黑模式
      currentSize: 'default', // 組件尺寸
      theme: {
        // 主題色
        elColorPrimary: '#409eff',
        // 左側菜單邊框顏色
        leftMenuBorderColor: 'inherit',
        // 左側菜單背景顏色
        leftMenuBgColor: '#001529',
        // 左側菜單淺色背景顏色
        leftMenuBgLightColor: '#0f2438',
        // 左側菜單選中背景顏色
        leftMenuBgActiveColor: 'var(--el-color-primary)',
        // 左側菜單收起選中背景顏色
        leftMenuCollapseBgActiveColor: 'var(--el-color-primary)',
        // 左側菜單字體顏色
        leftMenuTextColor: '#bfcbd9',
        // 左側菜單選中字體顏色
        leftMenuTextActiveColor: '#fff',
        // logo字體顏色
        logoTitleTextColor: '#fff',
        // logo邊框顏色
        logoBorderColor: 'inherit',
        // 頭部背景顏色
        topHeaderBgColor: '#fff',
        // 頭部字體顏色
        topHeaderTextColor: 'inherit',
        // 頭部懸停顏色
        topHeaderHoverColor: '#f6f6f6',
        // 頭部邊框顏色
        topToolBorderColor: '#eee'
      }
    }
  },
  getters: {
    /**是否為開發模式 */
    isDevelopment(): boolean {
      return this.NODE_ENV === 'development'
    },
    /**API 基礎路徑 */
    getApiBasePath(): string {
      return this.API_BASE_PATH
    },
    /**載入狀況 */
    isLoading(): boolean {
      return this.LoadingPromises.length > 0
    },
    getBreadcrumb(): boolean {
      return this.breadcrumb
    },
    getBreadcrumbIcon(): boolean {
      return this.breadcrumbIcon
    },
    getCollapse(): boolean {
      return this.collapse
    },
    getUniqueOpened(): boolean {
      return this.uniqueOpened
    },
    getHamburger(): boolean {
      return this.hamburger
    },
    getScreenfull(): boolean {
      return this.screenfull
    },
    getSize(): boolean {
      return this.size
    },
    getLocale(): boolean {
      return this.locale
    },
    getTagsView(): boolean {
      return this.tagsView
    },
    getTagsViewIcon(): boolean {
      return this.tagsViewIcon
    },
    getLogo(): boolean {
      return this.logo
    },
    getFixedHeader(): boolean {
      return this.fixedHeader
    },
    getGreyMode(): boolean {
      return this.greyMode
    },
    getDynamicRouter(): boolean {
      return this.dynamicRouter
    },
    getServerDynamicRouter(): boolean {
      return this.serverDynamicRouter
    },
    getFixedMenu(): boolean {
      return this.fixedMenu
    },
    getPageLoading(): boolean {
      return this.pageLoading
    },
    getLayout(): LayoutType {
      return this.layout
    },
    getTitle(): string {
      return this.title
    },
    getIsDark(): boolean {
      return this.isDark
    },
    getCurrentSize(): ComponentSize {
      return this.currentSize
    },
    getSizeMap(): ComponentSize[] {
      return this.sizeMap
    },
    getMobile(): boolean {
      return this.mobile
    },
    getTheme(): ThemeTypes {
      return this.theme
    },
    getFooter(): boolean {
      return this.footer
    }
  },
  actions: {
    setApiBasePath(path: string) {
      if (!path.endsWith('/')) {
        path += '/'
      }
      this.API_BASE_PATH = path
    },
    setLoadingPromise(loading: Promise<any>) {
      this.LoadingPromises.push(loading)
      loading.finally(() => {
        const index = this.LoadingPromises.indexOf(loading)
        if (index !== -1) {
          this.LoadingPromises.splice(index, 1)
        }
      })
    },

    setBreadcrumb(breadcrumb: boolean) {
      this.breadcrumb = breadcrumb
    },
    setBreadcrumbIcon(breadcrumbIcon: boolean) {
      this.breadcrumbIcon = breadcrumbIcon
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse
    },
    setUniqueOpened(uniqueOpened: boolean) {
      this.uniqueOpened = uniqueOpened
    },
    setHamburger(hamburger: boolean) {
      this.hamburger = hamburger
    },
    setScreenfull(screenfull: boolean) {
      this.screenfull = screenfull
    },
    setSize(size: boolean) {
      this.size = size
    },
    setLocale(locale: boolean) {
      this.locale = locale
    },
    setTagsView(tagsView: boolean) {
      this.tagsView = tagsView
    },
    setTagsViewIcon(tagsViewIcon: boolean) {
      this.tagsViewIcon = tagsViewIcon
    },
    setLogo(logo: boolean) {
      this.logo = logo
    },
    setFixedHeader(fixedHeader: boolean) {
      this.fixedHeader = fixedHeader
    },
    setGreyMode(greyMode: boolean) {
      this.greyMode = greyMode
    },
    setDynamicRouter(dynamicRouter: boolean) {
      this.dynamicRouter = dynamicRouter
    },
    setServerDynamicRouter(serverDynamicRouter: boolean) {
      this.serverDynamicRouter = serverDynamicRouter
    },
    setFixedMenu(fixedMenu: boolean) {
      this.fixedMenu = fixedMenu
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading
    },
    setLayout(layout: LayoutType) {
      if (this.mobile && layout !== 'classic') {
        ElMessage.warning('移動端模式下不支持切換其它布局')
        return
      }
      this.layout = layout
    },
    setTitle(title: string) {
      this.title = title
    },
    setIsDark(isDark: boolean) {
      this.isDark = isDark
      if (this.isDark) {
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
      } else {
        document.documentElement.classList.add('light')
        document.documentElement.classList.remove('dark')
      }
      this.setPrimaryLight()
    },
    setCurrentSize(currentSize: ComponentSize) {
      this.currentSize = currentSize
    },
    setMobile(mobile: boolean) {
      this.mobile = mobile
    },
    setTheme(theme: ThemeTypes) {
      this.theme = Object.assign(this.theme, theme)
    },
    setCssVarTheme() {
      for (const key in this.theme) {
        setCssVar(`--${humpToUnderline(key)}`, this.theme[key])
      }
      this.setPrimaryLight()
    },
    setFooter(footer: boolean) {
      this.footer = footer
    },
    setPrimaryLight() {
      if (this.theme.elColorPrimary) {
        const elColorPrimary = this.theme.elColorPrimary
        const color = this.isDark ? '#000000' : '#ffffff'
        const lightList = [3, 5, 7, 8, 9]
        lightList.forEach((v) => {
          setCssVar(`--el-color-primary-light-${v}`, mix(color, elColorPrimary, v / 10))
        })
        setCssVar(`--el-color-primary-dark-2`, mix(color, elColorPrimary, 0.2))
      }
    },
    setMenuTheme(color: string) {
      const primaryColor = useCssVar('--el-color-primary', document.documentElement)
      const isDarkColor = colorIsDark(color)
      const theme: Recordable = {
        // 左側菜單邊框顏色
        leftMenuBorderColor: isDarkColor ? 'inherit' : '#eee',
        // 左側菜單背景顏色
        leftMenuBgColor: color,
        // 左側菜單淺色背景顏色
        leftMenuBgLightColor: isDarkColor ? lighten(color!, 6) : color,
        // 左側菜單選中背景顏色
        leftMenuBgActiveColor: isDarkColor
          ? 'var(--el-color-primary)'
          : hexToRGB(unref(primaryColor) as string, 0.1),
        // 左側菜單收起選中背景顏色
        leftMenuCollapseBgActiveColor: isDarkColor
          ? 'var(--el-color-primary)'
          : hexToRGB(unref(primaryColor) as string, 0.1),
        // 左側菜單字體顏色
        leftMenuTextColor: isDarkColor ? '#bfcbd9' : '#333',
        // 左側菜單選中字體顏色
        leftMenuTextActiveColor: isDarkColor ? '#fff' : 'var(--el-color-primary)',
        // logo字體顏色
        logoTitleTextColor: isDarkColor ? '#fff' : 'inherit',
        // logo邊框顏色
        logoBorderColor: isDarkColor ? color : '#eee'
      }
      this.setTheme(theme)
      this.setCssVarTheme()
    },
    setHeaderTheme(color: string) {
      const isDarkColor = colorIsDark(color)
      const textColor = isDarkColor ? '#fff' : 'inherit'
      const textHoverColor = isDarkColor ? lighten(color!, 6) : '#f6f6f6'
      const topToolBorderColor = isDarkColor ? color : '#eee'
      setCssVar('--top-header-bg-color', color)
      setCssVar('--top-header-text-color', textColor)
      setCssVar('--top-header-hover-color', textHoverColor)
      this.setTheme({
        topHeaderBgColor: color,
        topHeaderTextColor: textColor,
        topHeaderHoverColor: textHoverColor,
        topToolBorderColor
      })
      if (this.getLayout === 'top') {
        this.setMenuTheme(color)
      }
    },
    initTheme() {
      const isDark = useDark({
        valueDark: 'dark',
        valueLight: 'light'
      })
      isDark.value = this.getIsDark
      // const newTitle = import.meta.env.VITE_APP_TITLE
      // newTitle !== this.getTitle && this.setTitle(newTitle)
      this.LoadingPromises = []
    }
  },
  persist: {
    key: `${securePiniaStorage.prefix('app')}`,
    storage: securePiniaStorage.storage
  }
})

export const useAppStoreWithOut = () => {
  return useAppStore(store)
}
